import { useCallback, useMemo, useState, type FC } from 'react';
import { SelectNoScrollCapture as Select } from '__components/Inputs/Select';
import type {
  AutomationVariableDataType,
  GenericOption,
  VariableCardinality,
  VariableCompatibility,
  VariableOption,
} from 'pages/AutomationEngine/types';
import { useTranslation } from 'react-i18next';
import type { VariableNameAndType } from '../VariableWizard/types';
import { useAutomationsSelector } from 'pages/AutomationEngine/store/react';
import { getAutomationIntializationVariables } from 'pages/AutomationEngine/store/selectors';
import BasicModal from '__components/Modals/presets/BasicModal';
import { VariableNameInput } from '../VariableNameInput';
import {
  AUTOMATION_VARIABLE_DATA_TYPES,
  VARIABLE_CARDINALITIES,
} from 'pages/AutomationEngine/constants';
import { useGetButtonGridOption } from '../VariableWizard/Components/GroupedButtonGrid/useGetButtonGridOption';
import { useQuery } from '@tanstack/react-query';
import { getVariableFormatMetaData } from '../VariableWizard/variable-types/format-meta-data';
import { allCustomObjectsQuery } from 'query/custom-object';
import { StyledNotice } from './styles';
import { getObjectKeys } from 'pages/AutomationEngine/ts-helpers';

export const AddVariableModal: FC<{
  show: boolean;
  onHide: () => void;
  onConfirm: (variable: VariableNameAndType, order: number) => void;
  allowedDataTypes: VariableCompatibility;
  allowedSubtype?: GenericOption;
  showCompatibilityNotice?: boolean;
}> = ({
  onHide,
  onConfirm,
  allowedDataTypes,
  allowedSubtype,
  showCompatibilityNotice,
}) => {
  const { t } = useTranslation();
  const variables: VariableOption[] = useAutomationsSelector(
    getAutomationIntializationVariables
  );
  const sections = useGetButtonGridOption();

  const [variable, setVariable] = useState<VariableNameAndType>(() => {
    const dataType =
      getObjectKeys(allowedDataTypes)[0] ||
      AUTOMATION_VARIABLE_DATA_TYPES.string;
    return {
      name: '',
      dataType,
      dataSubtype:
        dataType === AUTOMATION_VARIABLE_DATA_TYPES.entity
          ? allowedSubtype
          : undefined,
      isArray: allowedDataTypes[dataType]?.single
        ? false
        : !!allowedDataTypes[dataType]?.array,
      outputFormat:
        getVariableFormatMetaData(dataType)?.meta?.default_output_format,
      isArrayItemVariable: false,
      sourceArrayVariable: undefined,
    };
  });
  const [nameError, setNameError] = useState<string | null>(null);

  const { data: customObjectOptions = [] } = useQuery(
    allCustomObjectsQuery({
      staleTime: Infinity,
      select: (customObjects) => {
        return (customObjects || []).map(
          ({ id, object_name, entity_name }) => ({
            value: id,
            label: object_name,
            objectName: object_name,
            entityName: entity_name,
          })
        );
      },
    })
  );

  const filteredObjectOptions = useMemo(() => {
    if (allowedSubtype) {
      const customObject = customObjectOptions.find(
        (obj) => obj.value === allowedSubtype.value
      );
      return customObject ? [customObject] : [];
    }
    return customObjectOptions;
  }, [customObjectOptions, allowedSubtype]);

  const uniqueNames = useMemo(() => {
    return variables.map((v) => v.label.toLowerCase());
  }, [variables]);

  const [dataTypeOptions, dataTypeLookup] = useMemo(() => {
    const groupedOptions = sections
      .map((section) => ({
        ...section,
        options: section.options.reduce<
          GenericOption<AutomationVariableDataType>[]
        >((acc, option) => {
          if (allowedDataTypes[option.value]) {
            acc.push(option);
          }
          return acc;
        }, []),
      }))
      .filter((section) => section.options.length);

    const lookup = groupedOptions.reduce<
      Record<string, GenericOption<AutomationVariableDataType>>
    >((acc, section) => {
      section.options.forEach((option) => {
        acc[option.value] = option;
      });
      return acc;
    }, {});
    return [groupedOptions, lookup];
  }, [allowedDataTypes, sections]);

  const cardinalityOptions = useMemo(() => {
    const options: GenericOption<VariableCardinality>[] = [];

    if (allowedDataTypes[variable.dataType]?.[VARIABLE_CARDINALITIES.single]) {
      options.push({
        label: t('Single Value'),
        value: VARIABLE_CARDINALITIES.single,
      });
    }
    if (allowedDataTypes[variable.dataType]?.[VARIABLE_CARDINALITIES.array]) {
      options.push({
        label: t('Array of Values'),
        value: VARIABLE_CARDINALITIES.array,
      });
    }

    return options;
  }, [t, variable.dataType, allowedDataTypes]);

  const handleConfirm = () => {
    if (!variable.name) {
      setNameError(t('Variable name may not be blank.'));
      return;
    }
    onConfirm(variable, uniqueNames.length);
    onHide();
  };

  const handleChangeName = useCallback((name: string) => {
    setVariable((prev) => ({ ...prev, name }));
  }, []);

  const handleChangeDataType = useCallback(
    ({ value }: GenericOption<AutomationVariableDataType>) => {
      setVariable((prev) => ({
        ...prev,
        dataType: value,
        dataSubtype:
          value === AUTOMATION_VARIABLE_DATA_TYPES.entity
            ? prev.dataSubtype ?? allowedSubtype
            : undefined,
        isArray: allowedDataTypes[value]?.single
          ? false
          : !!allowedDataTypes[value]?.array,
        outputFormat:
          getVariableFormatMetaData(value)?.meta?.default_output_format,
      }));
    },
    [allowedSubtype, allowedDataTypes]
  );

  const handleChangeDataSubtype = useCallback((dataSubtype: GenericOption) => {
    setVariable((prev) => ({ ...prev, dataSubtype }));
  }, []);

  const handleChangeCardinality = useCallback(
    ({ value }: GenericOption<VariableCardinality>) => {
      setVariable((prev) => ({
        ...prev,
        isArray: value === VARIABLE_CARDINALITIES.array,
      }));
    },
    []
  );

  const isValid =
    !nameError &&
    variable.dataType &&
    (variable.dataType !== AUTOMATION_VARIABLE_DATA_TYPES.entity ||
      variable.dataSubtype);

  return (
    <BasicModal
      heading={t('Add Variable')}
      onConfirm={handleConfirm}
      onHide={onHide}
      buttonText={t('Save')}
      defaultLeftBtnText={t('Cancel')}
      show
      disabled={!isValid}
    >
      <VariableNameInput
        value={variable.name}
        onChange={handleChangeName}
        error={nameError}
        showError
        setError={setNameError}
        uniqueNames={uniqueNames}
        margin
      />
      {showCompatibilityNotice ? (
        <StyledNotice>
          {t('Only compatible variable types are selectable')}
        </StyledNotice>
      ) : null}
      <Select
        value={variable.dataType && dataTypeLookup[variable.dataType]}
        onChange={handleChangeDataType}
        options={dataTypeOptions}
        placeholder={t('Choose Variable Type')}
        label={t('Variable Type')}
        portal
        margin
      />
      {variable.dataType === AUTOMATION_VARIABLE_DATA_TYPES.entity ? (
        <>
          <Select
            label={t('Object')}
            placeholder={t('Choose Object')}
            value={variable.dataSubtype}
            options={filteredObjectOptions}
            onChange={handleChangeDataSubtype}
            portal
            margin
          />
          <div />
        </>
      ) : null}
      <Select
        label={t('Cardinality')}
        placeholder={t('Choose Cardinality')}
        labelInfo={t(
          'Once your configuration is saved, this value cannot be changed. Single values cannot be used where arrays are expected.'
        )}
        labelInfoPlacement="top"
        value={
          variable.isArray
            ? VARIABLE_CARDINALITIES.array
            : VARIABLE_CARDINALITIES.single
        }
        options={cardinalityOptions}
        onChange={handleChangeCardinality}
        portal
      />
    </BasicModal>
  );
};
